import { Checkbox, Dropdown, Menu } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { AnimatePresence } from 'framer-motion';
import { PureComponent, ReactNode } from 'react';
import { HxngMonetisation, isRXMT1, tooltipEnabledMonetization } from '../../../../pages/utils';
import { Prescription } from '../../../../store/types/prescription';
import { track } from '../../../../tracking';
import { MonetizeRxInfoModal } from './MonetizeRxInfoModal';

type Props = {
    type: HxngMonetisation;
    prescription: Prescription;
    isPharmaChecked?: boolean;
    isLabChecked?: boolean;
    isRXMTChecked?: boolean;
    rxMonetizationCheckboxToggler: (e: CheckboxChangeEvent, key: string) => void;
    initiatingRxMonetization: () => Promise<void>;
    isInitiatingRxMonetization?: boolean;
    monetizeAmount?: number;
    isFetchingCartDetails?: boolean;
};

type State = {
    isTooltipVisible: boolean;
    isMonetizeRxInfoModalVisible?: boolean;
};

export class MonetizeRxButton extends PureComponent<Props> {
    state: State = {
        isTooltipVisible: tooltipEnabledMonetization.has(this.props.type),
    };

    isMonetizeRxInfoModalVisibleToggleHandler = (): void => {
        const { prescription } = this.props;
        const { isMonetizeRxInfoModalVisible } = this.state;
        this.setState({
            isMonetizeRxInfoModalVisible: !isMonetizeRxInfoModalVisible,
        });
        track('MONETIZE_RX_INFO_MODAL', {
            pxId: prescription.id,
            action: isMonetizeRxInfoModalVisible ? 'close' : 'open',
        });
    };

    t1ButtonClickHandler = (): void => {
        const { initiatingRxMonetization } = this.props;
        const { isMonetizeRxInfoModalVisibleToggleHandler } = this;
        const today = new Date().toISOString().slice(0, 10);
        const rxMonetizationInfoShownDate = localStorage.getItem('rxMonetizationInfoShownDate');

        if (today === rxMonetizationInfoShownDate) {
            initiatingRxMonetization();
        } else {
            isMonetizeRxInfoModalVisibleToggleHandler();
            track('HXNG_MONETIZE_RX_BUTTON_1_CLICK');
        }
    };

    tooltipCloseHandler = (): void => {
        this.setState({ isTooltipVisible: false });
        track('TOOLTIP_CLOSE');
    };

    render(): ReactNode {
        const {
            type,
            isPharmaChecked,
            isLabChecked,
            isRXMTChecked,
            rxMonetizationCheckboxToggler,
            isInitiatingRxMonetization,
            initiatingRxMonetization,
            isFetchingCartDetails,
            monetizeAmount,
        } = this.props;
        const { isTooltipVisible, isMonetizeRxInfoModalVisible } = this.state;
        const {
            isMonetizeRxInfoModalVisibleToggleHandler,
            t1ButtonClickHandler,
            tooltipCloseHandler,
        } = this;

        const isLoading = isInitiatingRxMonetization;
        const disabled =
            isFetchingCartDetails ||
            isInitiatingRxMonetization ||
            (!isPharmaChecked && !isLabChecked);

        return (
            <div className="relative">
                {isTooltipVisible && (
                    <div
                        className="absolute w-320 space-y-1"
                        style={{ left: type === isRXMT1 ? '-60px' : '-50px', bottom: '45px' }}
                    >
                        <div
                            className="p-16 flex items-start space-x-12 rounded-8"
                            style={{
                                background: 'linear-gradient(136deg, #FFF8E8 0%, #F8E3AF 100%)',
                                border: '1px solid #F8DF9E',
                            }}
                        >
                            <div className="pt-4">
                                <svg
                                    className="w-14 h-20"
                                    viewBox="0 0 14 19"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M0.75 2C0.75 1.33594 1.29688 0.75 2 0.75H4.5H5.125H12C12.6641 0.75 13.25 1.33594 13.25 2C13.25 2.70312 12.6641 3.25 12 3.25H9.77344C10.1641 3.83594 10.4375 4.46094 10.5938 5.125H12C12.6641 5.125 13.25 5.71094 13.25 6.375C13.25 7.07812 12.6641 7.625 12 7.625H10.5938C10.0859 9.92969 8.17188 11.6875 5.82812 11.9609L11.4531 15.9844C12.0391 16.4141 12.1562 17.1953 11.7656 17.7422C11.3359 18.2891 10.5547 18.4453 10.0078 18.0156L1.25781 11.7656C0.828125 11.4531 0.632812 10.9062 0.789062 10.3984C0.945312 9.85156 1.45312 9.5 2 9.5H5.125C6.375 9.5 7.50781 8.75781 7.97656 7.625H2C1.29688 7.625 0.75 7.07812 0.75 6.375C0.75 5.71094 1.29688 5.125 2 5.125H7.97656C7.50781 4.03125 6.375 3.25 5.125 3.25H4.5H2C1.29688 3.25 0.75 2.70312 0.75 2Z"
                                        fill="#8E6807"
                                    />
                                </svg>
                            </div>

                            <div className="space-y-8 relative">
                                <p className="text-20 font-700">Monetize your Rx</p>
                                <p className="text-darwin-neutral-800">
                                    And{' '}
                                    <span className="font-700">
                                        earn
                                        {isFetchingCartDetails
                                            ? ' Rs. loading...'
                                            : monetizeAmount
                                            ? ' upto Rs. ' + monetizeAmount
                                            : ''}
                                    </span>{' '}
                                    per prescription with a click of a button!{' '}
                                    <span
                                        className="text-darwin-accent-bright-blue-800 underline cursor-pointer"
                                        onClick={isMonetizeRxInfoModalVisibleToggleHandler}
                                    >
                                        Learn more
                                    </span>
                                </p>

                                <div
                                    className="absolute -top-2 right-0 cursor-pointer"
                                    onClick={tooltipCloseHandler}
                                >
                                    <svg
                                        className="w-16 h-16 fill-darwin-neutral-900"
                                        viewBox="0 0 12 12"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path d="M10.75 9.71875C11.0312 10.0312 11.0312 10.5 10.75 10.7812C10.4375 11.0938 9.96875 11.0938 9.6875 10.7812L6 7.0625L2.28125 10.7812C1.96875 11.0938 1.5 11.0938 1.21875 10.7812C0.90625 10.5 0.90625 10.0312 1.21875 9.71875L4.9375 6L1.21875 2.28125C0.90625 1.96875 0.90625 1.5 1.21875 1.21875C1.5 0.90625 1.96875 0.90625 2.25 1.21875L6 4.96875L9.71875 1.25C10 0.9375 10.4688 0.9375 10.75 1.25C11.0625 1.53125 11.0625 2 10.75 2.3125L7.03125 6L10.75 9.71875Z" />
                                    </svg>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center justify-center">
                            <svg
                                className="w-18 h-12"
                                viewBox="0 0 17 12"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path d="M8.5 12L16.5 0H0.5L8.5 12Z" fill="#F8DF9E" />
                            </svg>
                        </div>
                    </div>
                )}

                <div className="flex items-center space-x-8">
                    {tooltipEnabledMonetization.has(type) && (
                        <Dropdown
                            overlay={
                                <Menu className="space-y-12 text-neutral-2 font-600 p-16 pr-8">
                                    <div className="font-700 pr-24">
                                        <p>Monetize for :</p>
                                    </div>
                                    {/* <div className="flex space-x-4 cursor-pointer ml-2">
                                        <Checkbox
                                            onChange={(e) =>
                                                rxMonetizationCheckboxToggler(e, 'pharma')
                                            }
                                            checked={isPharmaChecked}
                                            className="cursor-pointer"
                                        >
                                            Pharma
                                        </Checkbox>
                                    </div> */}
                                    <div className="flex space-x-4 cursor-pointer ml-2">
                                        <Checkbox
                                            onChange={(e) =>
                                                rxMonetizationCheckboxToggler(e, 'lab')
                                            }
                                            checked={isLabChecked}
                                            className="cursor-pointer"
                                        >
                                            Labs
                                        </Checkbox>
                                    </div>
                                </Menu>
                            }
                            placement="topCenter"
                            arrow
                            getPopupContainer={() =>
                                document.getElementById(`monetize-rx-bt-${type}`) || document.body
                            }
                        >
                            <button
                                className={`p-9 border border-darwin-accent-bright-blue-800 ${
                                    type === isRXMT1 ? 'bg-darwin-accent-bright-blue-800' : ''
                                } rounded-4`}
                                id={`monetize-rx-bt-${type}`}
                            >
                                <svg
                                    className="w-18 h-18"
                                    viewBox="0 0 16 16"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <mask
                                        id="mask0"
                                        mask-type="alpha"
                                        maskUnits="userSpaceOnUse"
                                        x="1"
                                        y="1"
                                        width="14"
                                        height="14"
                                    >
                                        <path
                                            fill-rule="evenodd"
                                            clip-rule="evenodd"
                                            d="M13.0014 7.99998C13.0014 8.22665 12.9814 8.43998 12.9547 8.65331L14.3614 9.75331C14.488 9.85331 14.5214 10.0333 14.4414 10.18L13.108 12.4866C13.028 12.6333 12.8547 12.6933 12.7014 12.6333L11.0414 11.9666C10.6947 12.2266 10.3214 12.4533 9.91469 12.62L9.66136 14.3866C9.64136 14.5466 9.50136 14.6666 9.33469 14.6666H6.66802C6.50136 14.6666 6.36136 14.5466 6.34136 14.3866L6.08802 12.62C5.68136 12.4533 5.30802 12.2333 4.96136 11.9666L3.30136 12.6333C3.15469 12.6866 2.97469 12.6333 2.89469 12.4866L1.56136 10.18C1.48136 10.0333 1.51469 9.85331 1.64136 9.75331L3.04802 8.65331C3.02136 8.43998 3.00136 8.21998 3.00136 7.99998C3.00136 7.77998 3.02136 7.55998 3.04802 7.34665L1.64136 6.24665C1.51469 6.14665 1.47469 5.96665 1.56136 5.81998L2.89469 3.51331C2.97469 3.36665 3.14802 3.30665 3.30136 3.36665L4.96136 4.03331C5.30802 3.77331 5.68136 3.54665 6.08802 3.37998L6.34136 1.61331C6.36136 1.45331 6.50136 1.33331 6.66802 1.33331H9.33469C9.50136 1.33331 9.64136 1.45331 9.66136 1.61331L9.91469 3.37998C10.3214 3.54665 10.6947 3.76665 11.0414 4.03331L12.7014 3.36665C12.848 3.31331 13.028 3.36665 13.108 3.51331L14.4414 5.81998C14.5214 5.96665 14.488 6.14665 14.3614 6.24665L12.9547 7.34665C12.9814 7.55998 13.0014 7.77331 13.0014 7.99998ZM5.66802 7.99998C5.66802 9.28665 6.71469 10.3333 8.00136 10.3333C9.28802 10.3333 10.3347 9.28665 10.3347 7.99998C10.3347 6.71331 9.28802 5.66665 8.00136 5.66665C6.71469 5.66665 5.66802 6.71331 5.66802 7.99998Z"
                                            fill="black"
                                        />
                                    </mask>
                                    <g mask="url(#mask0)">
                                        <rect
                                            width="16"
                                            height="16"
                                            fill="#fff"
                                            className={`${
                                                type === isRXMT1
                                                    ? 'fill-white'
                                                    : 'fill-darwin-accent-bright-blue-800'
                                            }`}
                                        />
                                    </g>
                                </svg>
                            </button>
                        </Dropdown>
                    )}

                    {type === isRXMT1 ? (
                        <button
                            onClick={t1ButtonClickHandler}
                            className={`flex items-center space-x-5 px-12 py-9 text-white bg-darwin-accent-bright-blue-800 rounded-4 ${
                                disabled ? 'opacity-70' : ''
                            }`}
                            disabled={disabled}
                        >
                            {isLoading ? (
                                <svg
                                    className="w-16 h-16 fill-white animate-spin"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 512 512"
                                >
                                    <path d="M96 256c0-26.5-21.5-48-48-48S0 229.5 0 256s21.5 48 48 48S96 282.5 96 256zM108.9 60.89c-26.5 0-48.01 21.49-48.01 47.99S82.39 156.9 108.9 156.9s47.99-21.51 47.99-48.01S135.4 60.89 108.9 60.89zM108.9 355.1c-26.5 0-48.01 21.51-48.01 48.01S82.39 451.1 108.9 451.1s47.99-21.49 47.99-47.99S135.4 355.1 108.9 355.1zM256 416c-26.5 0-48 21.5-48 48S229.5 512 256 512s48-21.5 48-48S282.5 416 256 416zM464 208C437.5 208 416 229.5 416 256s21.5 48 48 48S512 282.5 512 256S490.5 208 464 208zM403.1 355.1c-26.5 0-47.99 21.51-47.99 48.01S376.6 451.1 403.1 451.1s48.01-21.49 48.01-47.99S429.6 355.1 403.1 355.1zM256 0C229.5 0 208 21.5 208 48S229.5 96 256 96s48-21.5 48-48S282.5 0 256 0z"></path>
                                </svg>
                            ) : (
                                <svg
                                    className="w-16 h-16 fill-white"
                                    viewBox="0 0 13 13"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path d="M8.61719 4.08594L8.3125 2.9375L7.16406 2.63281C7.07031 2.58594 7 2.49219 7 2.375C7 2.28125 7.07031 2.1875 7.16406 2.14062L8.3125 1.8125L8.61719 0.6875C8.66406 0.59375 8.75781 0.5 8.875 0.5C8.96875 0.5 9.0625 0.59375 9.10938 0.6875L9.4375 1.8125L10.5625 2.14062C10.6562 2.1875 10.75 2.28125 10.75 2.375C10.75 2.49219 10.6562 2.58594 10.5625 2.63281L9.4375 2.9375L9.10938 4.08594C9.0625 4.17969 8.96875 4.25 8.875 4.25C8.75781 4.25 8.66406 4.17969 8.61719 4.08594ZM5.99219 4.20312L7.07031 6.40625L9.50781 6.75781C9.95312 6.82812 10.1406 7.39062 9.8125 7.71875L8.05469 9.42969L8.47656 11.8438C8.54688 12.3125 8.07812 12.6641 7.65625 12.4531L5.47656 11.3047L3.32031 12.4531C2.89844 12.6641 2.42969 12.3125 2.5 11.8438L2.92188 9.42969L1.16406 7.71875C0.835938 7.39062 1 6.82812 1.46875 6.75781L3.90625 6.40625L4.98438 4.20312C5.19531 3.78125 5.78125 3.78125 5.99219 4.20312ZM11.0312 6.21875L10.1406 5.96094C10.0469 5.9375 10 5.84375 10 5.75C10 5.67969 10.0469 5.58594 10.1406 5.5625L11.0312 5.30469L11.2891 4.41406C11.3125 4.32031 11.4062 4.25 11.5 4.25C11.5703 4.25 11.6641 4.32031 11.6875 4.41406L11.9453 5.30469L12.8359 5.5625C12.9297 5.58594 13 5.67969 13 5.75C13 5.84375 12.9297 5.9375 12.8359 5.96094L11.9453 6.21875L11.6875 7.10938C11.6641 7.20312 11.5703 7.25 11.5 7.25C11.4062 7.25 11.3125 7.20312 11.2891 7.10938L11.0312 6.21875Z" />
                                </svg>
                            )}

                            <span className="w-full text-center font-700">Monetize Rx</span>
                        </button>
                    ) : (
                        <Checkbox
                            checked={isRXMTChecked}
                            className={`select-none border border-darwin-accent-bright-blue-800 pl-16 pr-8 py-8 text-darwin-accent-bright-blue-800 font-700 rounded-4 ${
                                disabled ? 'opacity-70' : ''
                            }`}
                            onChange={(e) => rxMonetizationCheckboxToggler(e, 'rxmt')}
                            disabled={disabled}
                        >
                            Monetize Rx
                        </Checkbox>
                    )}
                </div>

                <AnimatePresence>
                    <MonetizeRxInfoModal
                        isVisible={isMonetizeRxInfoModalVisible}
                        closeMe={isMonetizeRxInfoModalVisibleToggleHandler}
                        monetizeAmount={monetizeAmount}
                        initiatingRxMonetization={initiatingRxMonetization}
                        isFetchingCartDetails={isFetchingCartDetails}
                        isLoading={isLoading}
                        disabled={disabled}
                        type={type}
                    />
                </AnimatePresence>
            </div>
        );
    }
}
